import { InLifeBreadcrumbs } from '@in-life/features/Breadcrumbs';
import { inLifePaths } from '@in-life/paths';
import { clearInLifeOrderAndRedirectToMyAccount } from '@in-life/utils/clearInLifeOrder';
import {
  BodyText,
  Box,
  Flex,
  Heading,
  Link,
} from '@utilitywarehouse/hearth-react';
import { ChevronLeftSmallIcon } from '@utilitywarehouse/hearth-react-icons';
import { useLocation } from 'react-router-dom';

import { DialogID, useDialog } from '@components/Dialog';
import type { HeaderProps } from '@components/Layout/Header';
import { Logo } from '@components/Layout/Header/Logo';
import { stripTrailingSlash } from '@utils/routing/stripTrailingSlash';

import { useCustomerProfileInfoContext } from '../../providers/CustomerProfileInfoProvider';
import './styles.scss';

const InLifeHeader = ({
  title,
  hideBackButton,
  onBack,
  backButtonLabel,
  isErrorBoundary,
}: HeaderProps) => {
  const { pathname } = useLocation();
  const { setOpen } = useDialog(DialogID.IN_LIFE_EXIT);
  const accountNumber = useCustomerProfileInfoContext()?.accountNumber ?? null;

  const strippedPathname = stripTrailingSlash(pathname);

  const handleStartAgain = () => {
    if (isErrorBoundary || strippedPathname === inLifePaths.thankYou) {
      clearInLifeOrderAndRedirectToMyAccount();
      return;
    }

    setOpen(true);
  };

  const showBreadcrumbs =
    !isErrorBoundary &&
    ![inLifePaths.thankYou, inLifePaths.energyDetails].includes(
      strippedPathname
    );

  return (
    <header className="inLifeHeader">
      <Flex direction="column" gap="400">
        <Box className="inLifeHeader__content" backgroundColor="brand">
          <Flex
            className="wrapper"
            justifyContent="between"
            alignItems="center"
          >
            <Logo onClick={handleStartAgain} />

            {!isErrorBoundary && accountNumber && (
              <BodyText
                color="inverted"
                data-testid="inlife-header-account-number"
              >
                Account number: {accountNumber}
              </BodyText>
            )}
          </Flex>
        </Box>

        <Flex className="wrapper" direction="column" spacing="lg">
          {showBreadcrumbs && <InLifeBreadcrumbs />}

          {onBack && !hideBackButton && (
            <Link
              className="layout-header__back"
              asChild
              data-testid="navigate-back-button"
              onClick={onBack}
            >
              <button type="button">
                <ChevronLeftSmallIcon />
                {backButtonLabel}
              </button>
            </Link>
          )}

          {title && (
            <Heading
              as="h1"
              size="xl"
              className="inLifeHeader__title"
              data-testid="inlife-header-title"
            >
              {title}
            </Heading>
          )}
        </Flex>
      </Flex>
    </header>
  );
};

export { InLifeHeader };
